// ============================================================
// TenantSearchTab.js
// Tenant Search tab inside <ClientDetailPage>. The user-visible surface
// for everything Phase 1 (pseudo-site filtering) + Phase 2a.1 (Broad
// consent flow) + Phase 2a.2 (CRUD + MCP brokers) built. Broad scope only
// (Selected is Phase 3).
//
// State is DERIVED from the ClientTenantIntegrations row via the pure
// deriveIntegrationState() below — five states (not_configured / active /
// degraded / revoked / expired). `pendingConsent` is component-local UI
// state (set when the admin generates a consent URL, cleared on the next
// refresh that returns a row) — NOT a derived/DB state.
//
// ── TWO ERROR PATTERNS (do not conflate — see api/adminTenantSearch.js) ──
//
//   Pattern A — CRUD (get/patch/revoke/reactivate/search-log/consent-url)
//     THROW on failure → try/catch → toast.error(crudErrorMessage(err)).
//     We branch on err.status (InvitationsPage pattern). We do NOT use
//     FederationTab's err.error mapping — ApiError has NO `.error` property
//     (its code is in err.message); that mapping is dead there.
//
//   Pattern B — MCP brokers (list-sites/test-search) RETURN the body at
//     HTTP 200: { success:true, ... } | { success:false, error:{code,message} }.
//     The MCP-call outcome is read off the result and shown INLINE + STICKY
//     (the CTI row state stays visible above). Only an endpoint-level HTTP
//     failure (the FA itself) throws → toast.
//
// Backend: backend/func-carehub-api/src/functions/admin-clients-tenant-integration-*.js
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import {
  RefreshCw, Copy, ExternalLink, AlertTriangle, AlertCircle, CheckCircle2, Search, Loader2,
} from 'lucide-react';
import Field from '../components/Field';
import Switch from '../components/Switch';
import Modal from '../components/Modal';
import { TenantIntegrationStatusBadge } from '../components/StatusBadge';
import { toast } from '../components/Toaster';
import { formatDate } from '../utils/format';
import {
  getTenantIntegration,
  getIndexingStatus,
  reindexClientDocuments,
  setClientDocumentIndexing,
  patchTenantIntegration,
  revokeTenantIntegration,
  reactivateTenantIntegration,
  getSearchLog,
  generateConsentUrl,
  listSites,
  testSearch,
  listGoogleDrives,
  saveGoogleWorkspace,
} from '../api/adminTenantSearch';
import StatusMessage from '../components/StatusMessage';
import './TenantSearchTab.css';

// Mirrors backend updateTenantIntegrationStatus.js:51 (DEGRADED_THRESHOLD=3).
// Update both if either changes. ConsecutiveFailureCount >= 5 expires the
// integration server-side (Status flips to 'expired'); 3–4 is the derived
// 'degraded' UI state while Status is still 'active'.
const DEGRADED_THRESHOLD = 3;
const GUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const LOG_LIMIT = 50;
const SCOPED_SITES_GATE = 30; // > this many sites: search activation locked until >=1 site selected

// Public app reg ID (non-secret — it appears in the consent URL the admin
// sees anyway). Mirrors the backend KV secret companion-app-selected-client-id.
// Used to pre-populate the Selected grant-script (D8b). Same for every client.
const COMPANION_SELECTED_APP_ID = '747c64a0-71a0-4bc7-8ad9-8c9312080606';

// ── Pure derivation: CTI row → state ────────────────────────
export function deriveIntegrationState(row) {
  if (!row) return 'not_configured';
  if (row.status === 'revoked') return 'revoked';
  if (row.status === 'expired') return 'expired';
  if (row.status === 'active') {
    return (row.consecutiveFailureCount || 0) >= DEGRADED_THRESHOLD ? 'degraded' : 'active';
  }
  return 'not_configured';
}

// ── Site-count curation nudge (Broad only) ──────────────────
// Conditioned on savedCount + search-active so the banner stops shouting
// "curate!" once the admin HAS curated (Phase 2a.3 polish #1). Returns
// 'hidden' (no banner) when curated AND search is on; 'curated' (a calm
// informational hint) when curated but search still off; else the size-
// based nudge, with 'red-gated' meaning the activation toggle is locked.
export function nudgeLevel(sitesLength, savedCount, isSearchActive) {
  if (savedCount > 0) return isSearchActive ? 'hidden' : 'curated';
  if (sitesLength <= 5) return 'green';
  if (sitesLength <= 15) return 'amber';
  if (sitesLength <= 30) return 'orange';
  return 'red-gated';
}
const NUDGE_TEXT = {
  green: 'Broad scope works well at this size — curation is optional.',
  amber: 'Curation recommended — selecting the document sites that matter makes searches faster and more reliable.',
  orange: 'Curation strongly recommended — many sites cause per-site timeouts. Pick the sites that hold real documents.',
  'red-gated': 'Curation required — there are too many sites to search reliably. Search activation is locked until you save at least one site.',
  // 'curated' + 'hidden' are rendered specially at the call site (curated
  // shows a dynamic "X of Y curated" hint; hidden renders no banner).
};

// ── Google Workspace connect flow (2026-09-09) ──────────────
// Replaces the Q19 read-only panel. The Drive twin of the M365 consent +
// picker, in TWO access modes (the row's subject column decides: NULL =
// direct):
//   direct (the default, ruled 2026-09-09): the client adds our service
//     account's address as a Viewer member of each Shared Drive and it
//     reads them AS ITSELF - no delegation grant, no licensed seat, no
//     admin-console step. "Load Shared Drives" lists the drives the
//     account can see whose organisers are in the client's domain (the
//     cross-client scope check; the backend re-verifies on save).
//   delegation (the fallback for a locked-down Workspace): the four-step
//     domain-wide-delegation checklist + a nominated account.
// Every value in either checklist comes from the LOADED key via the drives
// broker, never a literal here. Save goes through PUT /google-workspace
// (the shared upsert the ops script also uses).
const GOOGLE_DOMAIN_RE = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
const GOOGLE_EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const GOOGLE_CLIENT_INSTRUCTIONS_URL = 'https://dev.azure.com/a2zitai/carehub/_git/carehub?path=/docs/operations/google-workspace-client-instructions.md';
const GOOGLE_ENABLEMENT_URL = 'https://dev.azure.com/a2zitai/carehub/_git/carehub?path=/docs/operations/google-workspace-client-enablement.md';

// The drives broker's reasoned codes → what to redo. Each names ONE step so
// the operator is never sent to widen the wrong thing (the 2026-08-27
// shared-calendar lesson: a hedge pointing at two causes sends people to
// the one place the fault is not).
export function googleDrivesHint(error) {
  const code = error && error.code;
  if (code === 'consent_missing') return 'Google refused the delegation. Step 2 is not done, or the client ID / scope entered in the Google Admin console do not match the values above exactly.';
  if (code === 'subject_not_found') return 'Google does not know that account. Step 1: check the nominated account exists in this Workspace and the address is spelt exactly as Google shows it.';
  if (code === 'not_configured') return 'Our side is not set up: the platform\'s Google service account settings are missing. See the enablement runbook (A2Z IT action, not the client\'s).';
  if (code === 'google_timeout') return 'Google did not answer in time. Try again in a moment.';
  if (code === 'drive_not_verified') return (error && error.message) || 'One or more drives could not be attributed to the client\'s Workspace domain. Reload the drives and pick again.';
  return (error && error.message) || 'The Google Workspace check could not be completed. Please try again.';
}

// The access mode is DERIVED from the row's subject column (the backend
// does the same): NULL = the service account as itself.
export function googleAccessModeOf(integration) {
  if (!integration || integration.provider !== 'google_workspace') return 'direct';
  return integration.googleImpersonationSubject ? 'delegation' : 'direct';
}

function GoogleWorkspaceEditor({ clientId, integration, state, inFlight, onSaved, onToggleSearchActive, onCopy }) {
  const existing = integration && integration.provider === 'google_workspace' ? integration : null;
  const savedDrives = Array.isArray(existing?.scopedSites) ? existing.scopedSites : [];
  const editable = !existing || state === 'active' || state === 'degraded';
  const savedMode = googleAccessModeOf(existing);

  const [setup, setSetup] = useState(null);           // { configured, serviceAccountEmail, delegationClientId, scope, error } | null
  const [setupLoading, setSetupLoading] = useState(true);
  const [accessMode, setAccessMode] = useState(savedMode); // 'direct' | 'delegation'
  const [domain, setDomain] = useState(existing?.googleWorkspaceDomain || '');
  const [subject, setSubject] = useState(existing?.googleImpersonationSubject || '');
  const [unattributedCount, setUnattributedCount] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [drives, setDrives] = useState(savedDrives.length ? savedDrives : null); // null = not loaded yet
  const [drivesError, setDrivesError] = useState(null);
  const [drivesLoaded, setDrivesLoaded] = useState(false); // a real Load has succeeded this session
  const [selected, setSelected] = useState(() => new Set(savedDrives.map((d) => d.driveId)));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(null); // 'load' | 'save' | null

  // Setup facts (client ID + scope) - no subject, no token minted server-side.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await listGoogleDrives(clientId);
        if (!cancelled) setSetup(r);
      } catch (err) {
        if (!cancelled) setSetup({ configured: null, delegationClientId: null, scope: null, error: { code: 'unreachable', message: crudErrorMessage(err) } });
      } finally {
        if (!cancelled) setSetupLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  const isDirect = accessMode === 'direct';

  // Direct mode needs the domain for everything (it is the scope check);
  // delegation mode needs the account to load and both to save.
  function validate({ forSave }) {
    const errs = {};
    if ((isDirect || forSave) && !GOOGLE_DOMAIN_RE.test(domain.trim())) errs.domain = 'Enter the client\'s Workspace domain (for example theircare.co.uk).';
    if (!isDirect && !GOOGLE_EMAIL_RE.test(subject.trim())) errs.subject = 'Enter the nominated account\'s full email address.';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function switchMode(next) {
    if (next === accessMode) return;
    setAccessMode(next);
    setFieldErrors({});
    setDrivesError(null);
    setDrivesLoaded(false);
    setUnattributedCount(null);
  }

  async function handleLoadDrives() {
    if (!validate({ forSave: false })) return;
    setBusy('load');
    setDrivesError(null);
    setUnattributedCount(null);
    // A previous Load's "Connected" line must not survive a later failure
    // (seen live 2026-09-09: a wrong account showed the step-1 hint AND the
    // earlier success line together, which reads as both at once).
    setDrivesLoaded(false);
    try {
      const r = await listGoogleDrives(clientId, isDirect ? { domain: domain.trim() } : { subject: subject.trim() });
      if (!r || !r.success) {
        setDrivesError((r && r.error) || { code: 'unknown', message: 'The Google Workspace check could not be completed.' });
        return;
      }
      const loaded = Array.isArray(r.drives) ? r.drives : [];
      const loadedIds = new Set(loaded.map((d) => d.driveId));
      // Keep a saved drive the account can no longer see visible (flagged),
      // so an admin can tell "membership removed" from "never selected".
      const missing = savedDrives.filter((d) => selected.has(d.driveId) && !loadedIds.has(d.driveId)).map((d) => ({ ...d, missing: true }));
      setDrives([...loaded, ...missing]);
      setDrivesLoaded(true);
      if (isDirect && typeof r.unattributedCount === 'number') setUnattributedCount(r.unattributedCount);
    } catch (err) {
      setDrivesError({ code: 'http', message: crudErrorMessage(err) });
    } finally {
      setBusy(null);
    }
  }

  function toggleDrive(driveId) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(driveId)) next.delete(driveId); else next.add(driveId);
      return next;
    });
  }

  const selectedDrives = (drives || savedDrives).filter((d) => selected.has(d.driveId)).map((d) => ({ driveId: d.driveId, driveName: d.driveName || null }));
  const savedIds = savedDrives.map((d) => d.driveId).sort().join('|');
  const dirty = !existing
    || accessMode !== savedMode
    || domain.trim().toLowerCase() !== (existing.googleWorkspaceDomain || '').toLowerCase()
    || (!isDirect && subject.trim() !== (existing.googleImpersonationSubject || ''))
    || [...selected].sort().join('|') !== savedIds;
  const canSave = editable && dirty && selectedDrives.length > 0 && busy === null;

  async function handleSave() {
    if (!validate({ forSave: true })) return;
    if (selectedDrives.length === 0) {
      setFieldErrors((prev) => ({ ...prev, drives: 'Select at least one Shared Drive.' }));
      return;
    }
    setBusy('save');
    setDrivesError(null);
    try {
      const body = {
        domain: domain.trim(),
        drives: selectedDrives,
        // The upsert writes this flag from the body, so an edit must echo the
        // current value or it would silently switch search off.
        isSearchActive: !!(existing && existing.isSearchActive),
      };
      // No subject on the wire = direct-share mode; the row's subject goes NULL.
      if (!isDirect) body.subject = subject.trim();
      if (reason.trim()) body.reason = reason.trim();
      const row = await saveGoogleWorkspace(clientId, body);
      toast.success(row.created ? 'Google Workspace search connected for this client.' : 'Google Workspace settings saved.');
      setReason('');
      onSaved(row);
    } catch (err) {
      if (err && err.status === 409 && err.message === 'integration_provider_conflict') {
        toast.error('This client already has a Microsoft 365 tenant integration. Revoke it before connecting Google Workspace.');
      } else if (err && (err.message === 'drive_not_verified' || err.message === 'drive_verification_unavailable')) {
        // The backend's cross-client check refused: show it inline where the drives are.
        setDrivesError({ code: err.message, message: err.detail || err.hint || crudErrorMessage(err) });
      } else {
        toast.error(crudErrorMessage(err));
      }
    } finally {
      setBusy(null);
    }
  }

  const searchToggleDisabled = savedDrives.length === 0 || inFlight === 'toggle-search' || !editable;

  return (
    <div className="tenant-search__panel tenant-search__google" data-testid="google-workspace-editor">
      <h3 className="tenant-search__panel-title">{existing ? 'Google Workspace search' : 'Connect Google Workspace search'}</h3>
      <p className="tenant-search__panel-intro">
        Tenant search lets CareIQ agents cite the client's own documents. For a Google Workspace
        client that means their Shared Drives. The client adds A2Z CareHub's service account as
        a viewer of the drives to search; nothing else in their Workspace is ever visible. There
        is no consent screen, no admin-console step and no licensed seat.
      </p>

      {existing && (
        <dl className="tenant-search__google-facts" data-testid="google-facts">
          <div><dt>Status</dt><dd><TenantIntegrationStatusBadge state={state} consecutiveFailureCount={existing.consecutiveFailureCount} /></dd></div>
          <div><dt>Workspace domain</dt><dd>{existing.googleWorkspaceDomain || '—'}</dd></div>
          <div><dt>Access</dt><dd data-testid="google-facts-access">{savedMode === 'direct' ? 'Shared Drive membership' : 'Domain-wide delegation'}</dd></div>
          {savedMode === 'delegation' && <div><dt>Nominated account</dt><dd>{existing.googleImpersonationSubject || '—'}</dd></div>}
          <div><dt>Shared Drives in scope</dt><dd>{savedDrives.length}</dd></div>
          <div><dt>Search</dt><dd>{existing.isSearchActive ? 'Active' : 'Off'}</dd></div>
          {existing.lastSuccessAt && <div><dt>Last successful search</dt><dd>{formatDate(existing.lastSuccessAt)}</dd></div>}
        </dl>
      )}

      <fieldset className="tenant-search__scope-choice" data-testid="google-access-mode">
        <legend>Access method</legend>
        <label className="tenant-search__scope-option">
          <input type="radio" name="google-access-mode" value="direct" checked={isDirect} onChange={() => switchMode('direct')} disabled={!editable} data-testid="google-access-mode-direct" />
          <span>
            <span className="tenant-search__scope-name">Shared Drive membership (recommended)</span>
            <span className="tenant-search__scope-desc">The client adds our service account as a viewer of each drive. No delegation grant, no nominated account, no licence.</span>
          </span>
        </label>
        <label className="tenant-search__scope-option">
          <input type="radio" name="google-access-mode" value="delegation" checked={!isDirect} onChange={() => switchMode('delegation')} disabled={!editable} data-testid="google-access-mode-delegation" />
          <span>
            <span className="tenant-search__scope-name">Domain-wide delegation (fallback)</span>
            <span className="tenant-search__scope-desc">For a Workspace whose external sharing is locked down: a super-admin grant plus a nominated account we act as.</span>
          </span>
        </label>
      </fieldset>

      <h4>Setup checklist (the client's side)</h4>
      {isDirect ? (
        <ol className="tenant-search__checklist" data-testid="google-checklist">
          <li>
            <strong>Share the drives with A2Z CareHub.</strong> On each Shared Drive to search:
            Manage members → add this address as a <em>Viewer</em>. Google warns that it is
            outside their organisation; they choose Share anyway.
            {setupLoading ? (
              <p className="tenant-search__hint"><Loader2 size={14} className="tenant-search__spin" /> Loading the address…</p>
            ) : setup && setup.serviceAccountEmail ? (
              <div className="tenant-search__checklist-artifacts">
                <div className="tenant-search__artifact">
                  <span className="tenant-search__artifact-label">Address</span>
                  <pre className="tenant-search__code" data-testid="google-setup-service-account">{setup.serviceAccountEmail}</pre>
                  <button type="button" className="button-secondary" onClick={() => onCopy(setup.serviceAccountEmail)} aria-label="Copy service account address"><Copy size={14} /> Copy</button>
                </div>
              </div>
            ) : (
              <p className="tenant-search__hint tenant-search__hint--error" data-testid="google-setup-error">
                <AlertCircle size={14} /> {googleDrivesHint(setup && setup.error)}
              </p>
            )}
          </li>
          <li>
            <strong>Tell A2Z IT their Workspace domain</strong> (the part after the @ in their
            staff email addresses), then load the drives below. Only drives managed by someone
            in that domain are offered, so a drive can never be attached to the wrong client.
          </li>
        </ol>
      ) : (
        <ol className="tenant-search__checklist" data-testid="google-checklist">
          <li>
            <strong>Create the nominated account.</strong> An ordinary Workspace user in the client's
            domain, e.g. <code>carehub-search@theirdomain.co.uk</code>. It takes a licensed seat and
            nobody signs in to it; an existing staff account works too.
          </li>
          <li>
            <strong>Authorise A2Z CareHub for domain-wide delegation.</strong> Google Admin console
            → Security → Access and data control → API controls → Manage domain-wide delegation
            → Add new. Paste exactly:
            {setupLoading ? (
              <p className="tenant-search__hint"><Loader2 size={14} className="tenant-search__spin" /> Loading the values…</p>
            ) : setup && setup.delegationClientId ? (
              <div className="tenant-search__checklist-artifacts">
                <div className="tenant-search__artifact">
                  <span className="tenant-search__artifact-label">Client ID</span>
                  <pre className="tenant-search__code" data-testid="google-setup-client-id">{setup.delegationClientId}</pre>
                  <button type="button" className="button-secondary" onClick={() => onCopy(setup.delegationClientId)} aria-label="Copy client ID"><Copy size={14} /> Copy</button>
                </div>
                <div className="tenant-search__artifact">
                  <span className="tenant-search__artifact-label">OAuth scope</span>
                  <pre className="tenant-search__code" data-testid="google-setup-scope">{setup.scope}</pre>
                  <button type="button" className="button-secondary" onClick={() => onCopy(setup.scope)} aria-label="Copy OAuth scope"><Copy size={14} /> Copy</button>
                </div>
              </div>
            ) : (
              <p className="tenant-search__hint tenant-search__hint--error" data-testid="google-setup-error">
                <AlertCircle size={14} /> {googleDrivesHint(setup && setup.error)}
              </p>
            )}
          </li>
          <li>
            <strong>Share the drives.</strong> Add the nominated account as a <em>Viewer</em> on every
            Shared Drive CareHub should search (Shared Drive → Manage members). Nothing outside
            those drives is ever read.
          </li>
          <li>
            <strong>Tell A2Z IT the domain and the account address</strong>, then load the drives below.
          </li>
        </ol>
      )}
      <p className="tenant-search__hint">
        <a href={GOOGLE_CLIENT_INSTRUCTIONS_URL} target="_blank" rel="noopener noreferrer" data-testid="google-client-instructions-link">
          <ExternalLink size={14} /> Client-facing instructions to send them
        </a>
        <span className="tenant-search__muted"> · </span>
        <a href={GOOGLE_ENABLEMENT_URL} target="_blank" rel="noopener noreferrer">
          <ExternalLink size={14} /> A2Z IT enablement runbook
        </a>
      </p>

      {!editable && (
        <p className="tenant-search__hint" data-testid="google-editor-locked">
          <AlertCircle size={14} /> This integration is {state}. Reactivate it below before changing the drives.
        </p>
      )}

      <Field label="Workspace domain" error={fieldErrors.domain} required>
        <input
          type="text"
          value={domain}
          onChange={(e) => { setDomain(e.target.value); if (fieldErrors.domain) setFieldErrors((p) => ({ ...p, domain: undefined })); }}
          placeholder="theircare.co.uk"
          autoComplete="off"
          disabled={!editable}
          data-testid="google-domain"
        />
      </Field>
      {!isDirect && (
        <Field label="Nominated account" error={fieldErrors.subject} required>
          <input
            type="email"
            value={subject}
            onChange={(e) => { setSubject(e.target.value); if (fieldErrors.subject) setFieldErrors((p) => ({ ...p, subject: undefined })); }}
            placeholder="carehub-search@theircare.co.uk"
            autoComplete="off"
            disabled={!editable}
            data-testid="google-subject"
          />
        </Field>
      )}

      <div className="tenant-search__picker-actions">
        <button
          type="button"
          className="button-secondary"
          onClick={handleLoadDrives}
          disabled={!editable || busy !== null}
          data-testid="google-load-drives"
        >
          {busy === 'load' ? <><Loader2 size={14} className="tenant-search__spin" /> Checking…</> : <><Search size={14} /> Load Shared Drives</>}
        </button>
        <span className="tenant-search__muted">
          {isDirect
            ? 'Also the connection test: it lists the drives the service account has been added to, managed by someone in this domain.'
            : 'Also the connection test: it only succeeds once steps 1 to 3 are done.'}
        </span>
      </div>

      {drivesError && (
        <p className="tenant-search__hint tenant-search__hint--error" role="alert" data-testid="google-drives-error">
          <AlertCircle size={14} /> {googleDrivesHint(drivesError)}
        </p>
      )}

      {drives && (
        <>
          {drivesLoaded && (
            <p className="tenant-search__resolve-ok" data-testid="google-drives-ok">
              <CheckCircle2 size={14} /> Connected. {drives.filter((d) => !d.missing).length === 0
                ? (isDirect
                  ? `The service account has not been added to any Shared Drive managed from ${domain.trim() || 'this domain'} yet (step 1).`
                  : 'The account is a member of no Shared Drive yet (step 3).')
                : `${isDirect ? 'The service account' : 'The account'} can see ${drives.filter((d) => !d.missing).length} Shared Drive${drives.filter((d) => !d.missing).length === 1 ? '' : 's'}.`}
              {isDirect && unattributedCount > 0 && (
                <span className="tenant-search__muted" data-testid="google-drives-unattributed">
                  {' '}{unattributedCount} other drive{unattributedCount === 1 ? '' : 's'} shared with the service account {unattributedCount === 1 ? 'is' : 'are'} managed from another domain and not shown.
                </span>
              )}
            </p>
          )}
          {drives.length > 0 && (
            <ul className="tenant-search__site-list" data-testid="google-drive-list">
              {drives.map((d) => (
                <li key={d.driveId} className={`tenant-search__site-row${d.missing ? ' tenant-search__site-row--dropped' : ''}`} data-testid="google-drive-row">
                  <label className="tenant-search__site-label">
                    <input
                      type="checkbox"
                      checked={selected.has(d.driveId)}
                      onChange={() => toggleDrive(d.driveId)}
                      disabled={!editable}
                    />
                    <span className="tenant-search__site-name">{d.driveName || d.driveId}</span>
                    <span className="tenant-search__site-url">{d.driveId}{d.missing ? ' · the account is no longer a member of this drive' : ''}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {fieldErrors.drives && <p className="tenant-search__hint tenant-search__hint--error" data-testid="google-drives-required">{fieldErrors.drives}</p>}

      {editable && (
        <>
          <Field label="Reason (optional, recorded in the audit log)">
            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} autoComplete="off" data-testid="google-save-reason" />
          </Field>
          <div className="tenant-search__picker-actions">
            <button type="button" className="button-primary" onClick={handleSave} disabled={!canSave} data-testid="google-save">
              {busy === 'save' ? 'Saving…' : existing ? 'Save changes' : 'Connect Google Workspace'}
            </button>
            {!existing && <span className="tenant-search__muted">Search stays off until you switch it on after connecting.</span>}
          </div>
        </>
      )}

      {existing && (
        <div className="tenant-search__toggles">
          <div className="tenant-search__toggle-row">
            <Switch
              checked={!!existing.isSearchActive}
              onChange={onToggleSearchActive}
              disabled={searchToggleDisabled}
              ariaLabel="Search active"
              id="tenant-search-google-search-active"
            />
            <label htmlFor="tenant-search-google-search-active" className="tenant-search__toggle-label">
              Search active
              {savedDrives.length === 0 && (
                <span className="tenant-search__toggle-hint" data-testid="google-search-gate-hint"> — save at least one Shared Drive before activating search</span>
              )}
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

function getScopeLabel(scopeModel) {
  if (scopeModel === 'broad') return 'Broad (all sites)';
  if (scopeModel === 'selected') return 'Selected (per-site grants)';
  return scopeModel || 'Unknown'; // shouldn't happen — CTI ScopeModel is CHECK-constrained
}

// Build the Selected grant-script (D8b). Composed as an ARRAY of single-
// quoted JS strings joined with newlines — NOT a template literal — because
// the PowerShell body contains literal `$`, `$(...)` and would clash with
// template-literal `${}`/backtick syntax. Only tenant/appId/urls are
// interpolated (by concatenation); the script uses TrimEnd("/") (double
// quotes) so there are zero single quotes to escape inside the JS strings.
function buildGrantScript(tenantId, urls) {
  const urlLines = (urls.length > 0 ? urls : ['https://your-tenant.sharepoint.com/sites/YourSite'])
    .map((u) => '  "' + u + '",');
  return [
    '# A2Z CareHub — grant the Selected document-intel app read access to chosen SharePoint sites.',
    '# Run as a SharePoint Administrator or Global Administrator in the client M365 tenant.',
    '# Requires the Microsoft.Graph PowerShell module (Install-Module Microsoft.Graph).',
    '',
    'Connect-MgGraph -TenantId "' + (tenantId || '<client-tenant-id>') + '" -Scopes "Sites.FullControl.All"',
    '',
    '$appId   = "' + COMPANION_SELECTED_APP_ID + '"',
    '$appName = "A2Z CareHub Document Intel (Selected)"',
    '',
    '# Site URLs to grant (edit as needed):',
    '$siteUrls = @(',
    ...urlLines,
    ')',
    '',
    'foreach ($url in $siteUrls) {',
    '  try {',
    '    $u        = [System.Uri] $url',
    '    $hostName = $u.Host',
    '    $relPath  = $u.AbsolutePath.TrimEnd("/")',
    '    $site = Invoke-MgGraphRequest -Method GET -Uri ("https://graph.microsoft.com/v1.0/sites/" + $hostName + ":" + $relPath)',
    '    $body = @{ roles = @("read"); grantedToIdentitiesV2 = @(@{ application = @{ id = $appId; displayName = $appName } }) } | ConvertTo-Json -Depth 5',
    '    Invoke-MgGraphRequest -Method POST -Uri ("https://graph.microsoft.com/v1.0/sites/" + $site.id + "/permissions") -Body $body -ContentType "application/json"',
    '    Write-Host "GRANTED  $url" -ForegroundColor Green',
    '  } catch {',
    '    Write-Host "FAILED   $url  ->  $($_.Exception.Message)" -ForegroundColor Red',
    '  }',
    '}',
    'Write-Host "Done. Return to the admin portal and click Resolve sites." -ForegroundColor Cyan',
  ].join('\n');
}

// CRUD error → operator message (InvitationsPage pattern; status-based).
function crudErrorMessage(err) {
  if (!err) return 'Something went wrong. Please try again.';
  if (err.isNetworkError) return 'Could not reach the API. Check your connection and try again.';
  if (err.status === 404) return 'This integration no longer exists. Refresh to see the current state.';
  if (err.status === 409) return err.detail || err.message || 'That action conflicts with the current state. Refresh and try again.';
  if (err.status === 400) return err.detail || err.message || 'The request was rejected. Check the values and try again.';
  if (err.status === 500) return 'Something went wrong on our end. Please try again.';
  return err.message || 'Something went wrong. Please try again.';
}

function mcpErrorText(error) {
  return (error && error.message) || 'The tenant search service could not be reached. Please try again.';
}

export default function TenantSearchTab({ clientId }) {
  // ── Lifecycle / fetch ──────────────────────────────────────
  const [integration, setIntegration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [inFlight, setInFlight] = useState(null); // 'consent'|'list-sites'|'save-sites'|'toggle-search'|'test-search'|'revoke'|'reactivate'
  // Optional audit annotation carried on a scoped-sites save (the backend has
  // accepted `reason` since 2026-07-21; nothing sent one until now).
  const [saveReason, setSaveReason] = useState('');

  // ── Consent (component-local) ──────────────────────────────
  const [pendingConsent, setPendingConsent] = useState(false);
  const [scopeChoice, setScopeChoice] = useState('broad'); // radio at not_configured (D8a); default broad, no pre-commit
  const [providerChoice, setProviderChoice] = useState('m365'); // not_configured only: which connect flow to show (2026-09-09)
  const [consentTenantId, setConsentTenantId] = useState('');
  const [consentTenantIdError, setConsentTenantIdError] = useState(null);
  const [consentInfo, setConsentInfo] = useState(null); // { consentUrl, expiresAt, correlationId }

  // ── ScopedSites picker — Broad enumeration flow ────────────
  const [sites, setSites] = useState(null); // null = not loaded
  const [pseudoSitesFiltered, setPseudoSitesFiltered] = useState(0);
  const [sitesError, setSitesError] = useState(null); // { code, message } | null — sticky
  const [siteSearchTerm, setSiteSearchTerm] = useState('');
  const [selectedSiteIds, setSelectedSiteIds] = useState(() => new Set()); // Broad: user-checkbox selection

  // ── Selected URL-resolve flow (Phase 3a) ───────────────────
  // Named to avoid collision with Broad's selectedSiteIds — here "Selected"
  // is the SCOPE name, not a user selection. resolveTextarea = raw input;
  // resolvedSites/droppedUrls = the last Resolve outcome.
  const [resolveTextarea, setResolveTextarea] = useState('');
  const [resolvedSites, setResolvedSites] = useState(null); // null = not resolved yet
  const [droppedUrls, setDroppedUrls] = useState([]);

  // ── Test search ────────────────────────────────────────────
  const [testQuery, setTestQuery] = useState('');

  // ── Phase 4 — document content indexing ────────────────────
  const [indexing, setIndexing] = useState(null);      // status payload | null
  const [indexingError, setIndexingError] = useState(null);
  const [testResult, setTestResult] = useState(null); // { success, diagnostics } | null
  const [testError, setTestError] = useState(null); // { code, message } | null — sticky

  // ── Search log ─────────────────────────────────────────────
  const [logItems, setLogItems] = useState([]);
  const [logPagination, setLogPagination] = useState(null);
  const [logOffset, setLogOffset] = useState(0);
  const [logFilters, setLogFilters] = useState({ agentType: '', fromDate: '', toDate: '', onlyErrors: false });
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState(null);

  // ── Reason-required confirm modal ──────────────────────────
  const [confirmKind, setConfirmKind] = useState(null); // 'revoke' | 'reactivate' | null

  const fetchIntegration = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const row = await getTenantIntegration(clientId);
      setIntegration(row);
      if (row) {
        setPendingConsent(false);
        const saved = row.scopedSites || [];
        setSelectedSiteIds(new Set(saved.map((s) => s.siteId)));
        if (row.scopeModel === 'selected') {
          // Seed the Selected flow from the saved curation so the admin sees
          // their curated sites + a pre-filled URL textarea on load.
          setResolvedSites(saved);
          setResolveTextarea(saved.map((s) => s.siteUrl).filter(Boolean).join('\n'));
        }
      }
    } catch (err) {
      setLoadError(err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetchIntegration(); }, [fetchIntegration]);

  const state = deriveIntegrationState(integration);
  const tenantIntegrationId = integration ? integration.tenantIntegrationId : null;
  // scopeModel is hoisted here because Phase 3a Selected branching reads it
  // from multiple render points (GrantScriptPanel, ScopedSitesPicker mode,
  // disabled toggle, disabled TestSearchPanel). Before Phase 3a, no render
  // branched on scope, so it lived only inside IntegrationStatusHeader's display.
  const scopeModel = integration?.scopeModel ?? null;

  // ── Search log auto-load (when an integration exists) ──────
  const loadSearchLog = useCallback(async (offset, filters) => {
    if (!tenantIntegrationId) return;
    setLogLoading(true);
    setLogError(null);
    try {
      const res = await getSearchLog(clientId, tenantIntegrationId, {
        limit: LOG_LIMIT,
        offset,
        agentType: filters.agentType || undefined,
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
        onlyErrors: filters.onlyErrors || undefined,
      });
      setLogItems(res.items || []);
      setLogPagination(res.pagination || null);
    } catch (err) {
      setLogError(err);
    } finally {
      setLogLoading(false);
    }
  }, [clientId, tenantIntegrationId]);

  useEffect(() => {
    if (tenantIntegrationId) loadSearchLog(logOffset, logFilters);
  }, [tenantIntegrationId, logOffset, logFilters, loadSearchLog]);

  // ── Consent ────────────────────────────────────────────────
  async function handleGenerateConsentUrl() {
    if (!GUID_RE.test(consentTenantId.trim())) {
      setConsentTenantIdError('Enter the client’s M365 tenant GUID.');
      return;
    }
    setConsentTenantIdError(null);
    setInFlight('consent');
    try {
      const info = await generateConsentUrl(clientId, { tenantId: consentTenantId.trim(), scopeModel: scopeChoice });
      setConsentInfo(info);
      setPendingConsent(true);
    } catch (err) {
      toast.error(crudErrorMessage(err));
    } finally {
      setInFlight(null);
    }
  }

  async function copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard.');
    } catch (_e) {
      toast.error('Copy failed — select and copy manually.');
    }
  }

  // ── Sites picker ───────────────────────────────────────────
  async function handleLoadSites() {
    setInFlight('list-sites');
    setSitesError(null);
    try {
      const res = await listSites(clientId, tenantIntegrationId, siteSearchTerm.trim() || undefined);
      if (res.success) {
        setSites(res.sites || []);
        setPseudoSitesFiltered(res.pseudoSitesFiltered || 0);
      } else {
        setSitesError(res.error || { code: 'unknown', message: 'Failed to load sites.' });
      }
    } catch (err) {
      toast.error(crudErrorMessage(err));
    } finally {
      setInFlight(null);
    }
  }

  function toggleSite(siteId) {
    setSelectedSiteIds((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId); else next.add(siteId);
      return next;
    });
  }

  async function handleSaveScopedSites() {
    // Broad saves the user-checkbox selection from the enumerated list;
    // Selected saves the resolved set (the URL list IS the curation, D8c).
    const chosen = scopeModel === 'selected'
      ? (resolvedSites || []).map((s) => ({ siteId: s.siteId, siteName: s.siteName, siteUrl: s.siteUrl }))
      : (sites || []).filter((s) => selectedSiteIds.has(s.siteId)).map((s) => ({ siteId: s.siteId, siteName: s.siteName, siteUrl: s.siteUrl }));
    setInFlight('save-sites');
    try {
      // `reason` is an OPTIONAL audit annotation, deliberately not required.
      // Revoke/reactivate demand one because they change what a client can
      // do; curating sites is routine, reversible editing, and a mandatory
      // box on every save trains people to type "update" to get past it.
      // Omitted entirely when blank — the handler rejects a body carrying
      // only a reason, so an empty string must never be sent.
      const body = { scopedSites: chosen };
      const trimmedReason = saveReason.trim();
      if (trimmedReason) body.reason = trimmedReason;
      const updated = await patchTenantIntegration(clientId, tenantIntegrationId, body);
      setSaveReason('');
      setIntegration(updated);
      const savedNow = updated.scopedSites || [];
      setSelectedSiteIds(new Set(savedNow.map((s) => s.siteId)));
      if (scopeModel === 'selected') setResolvedSites(savedNow);
      toast.success(`Saved ${chosen.length} site${chosen.length === 1 ? '' : 's'}.`);
    } catch (err) {
      toast.error(crudErrorMessage(err));
    } finally {
      setInFlight(null);
    }
  }

  // Selected: resolve the admin-supplied URLs (textarea, one per line) via the
  // list-sites broker's URL-resolve mode. Pattern B — the body is returned
  // verbatim; resolved sites + droppedUrls render below the textarea (D8c).
  async function handleResolveSites() {
    const urls = resolveTextarea.split('\n').map((u) => u.trim()).filter(Boolean);
    setInFlight('resolve-sites');
    setSitesError(null);
    try {
      const res = await listSites(clientId, tenantIntegrationId, undefined, urls);
      if (res.success) {
        setResolvedSites(res.sites || []);
        setDroppedUrls(res.droppedUrls || []);
      } else {
        setSitesError(res.error || { code: 'unknown', message: 'Failed to resolve sites.' });
      }
    } catch (err) {
      toast.error(crudErrorMessage(err));
    } finally {
      setInFlight(null);
    }
  }

  async function handleToggleSearchActive(next) {
    setInFlight('toggle-search');
    try {
      const updated = await patchTenantIntegration(clientId, tenantIntegrationId, { isSearchActive: next });
      setIntegration(updated);
      toast.success(next ? 'Search activated for this tenant.' : 'Search deactivated.');
    } catch (err) {
      toast.error(crudErrorMessage(err));
    } finally {
      setInFlight(null);
    }
  }

  // ── Phase 4 — indexing ─────────────────────────────────────
  const loadIndexingStatus = useCallback(async () => {
    try {
      const status = await getIndexingStatus(clientId);
      setIndexing(status);
      setIndexingError(null);
    } catch (err) {
      // Non-fatal: the rest of the tab must still work if this read fails.
      setIndexingError(crudErrorMessage(err));
    }
  }, [clientId]);

  // Placed AFTER the useCallback above, deliberately: loadIndexingStatus is a
  // const, so an effect referencing it from higher up the component hits the
  // temporal dead zone and throws on first render (caught by the tab tests).
  // Phase 4 status is a SEPARATE read — the tab renders fully even if it fails.
  useEffect(() => { loadIndexingStatus(); }, [loadIndexingStatus]);

  async function handleToggleIndexingActive(next) {
    setInFlight('toggle-indexing');
    try {
      // CLIENT-level switch (ruled 2026-08-10) - deliberately NOT the
      // tenant-integration PATCH, so this works for a client with no M365
      // integration at all. The panel reads its state from the status
      // payload rather than the integration row for the same reason.
      await setClientDocumentIndexing(clientId, next);
      toast.success(next
        ? 'Document content indexing switched on. Use Re-index to ingest existing documents.'
        : 'Document content indexing switched off. Agents fall back to document titles.');
      await loadIndexingStatus();
    } catch (err) {
      toast.error(crudErrorMessage(err));
    } finally {
      setInFlight(null);
    }
  }

  async function handleReindex() {
    setInFlight('reindex');
    try {
      const res = await reindexClientDocuments(clientId);
      if (res && res.enqueuedCount > 0) {
        toast.success(res.detail || `${res.enqueuedCount} document(s) queued for re-indexing.`);
      } else {
        // Not an error: a client with no active policies has nothing to index.
        toast.info(res && res.detail ? res.detail : 'No eligible documents to re-index.');
      }
      await loadIndexingStatus();
    } catch (err) {
      toast.error(crudErrorMessage(err));
    } finally {
      setInFlight(null);
    }
  }

  // ── Test search ────────────────────────────────────────────
  async function handleTestSearch() {
    setInFlight('test-search');
    setTestError(null);
    setTestResult(null);
    try {
      const res = await testSearch(clientId, tenantIntegrationId, testQuery.trim() || undefined);
      // Distinguish an MCP-call FAILURE (has `error`) from a ran-but-partial
      // diagnostic outcome (has `diagnostics`, success may be false).
      if (res && res.error) {
        setTestError(res.error);
      } else {
        setTestResult(res);
      }
    } catch (err) {
      toast.error(crudErrorMessage(err));
    } finally {
      setInFlight(null);
    }
  }

  // ── Revoke / reactivate ────────────────────────────────────
  async function handleRevoke(reason) {
    setInFlight('revoke');
    try {
      const updated = await revokeTenantIntegration(clientId, tenantIntegrationId, reason);
      setIntegration(updated);
      setConfirmKind(null);
      toast.success('Tenant search revoked.');
    } catch (err) {
      toast.error(crudErrorMessage(err));
    } finally {
      setInFlight(null);
    }
  }

  async function handleReactivate(reason) {
    setInFlight('reactivate');
    try {
      const updated = await reactivateTenantIntegration(clientId, tenantIntegrationId, reason);
      setIntegration(updated);
      setConfirmKind(null);
      toast.success('Tenant search reactivated. Search stays off until you switch it on.');
    } catch (err) {
      toast.error(crudErrorMessage(err));
    } finally {
      setInFlight(null);
    }
  }

  // ── Search log filter / pagination ─────────────────────────
  function updateLogFilter(patch) {
    setLogOffset(0);
    setLogFilters((prev) => ({ ...prev, ...patch }));
  }
  function viewErrorsInLog() {
    setLogOffset(0);
    setLogFilters((prev) => ({ ...prev, onlyErrors: true }));
    // Bring the log into view.
    const el = document.getElementById('tenant-search-log');
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ── Render ─────────────────────────────────────────────────
  if (loading) {
    return <StatusMessage variant="loading" className="tenant-search__state">Loading tenant search…</StatusMessage>;
  }
  if (loadError) {
    return (
      <StatusMessage variant="error" className="tenant-search__state tenant-search__state--error">
        <p>{crudErrorMessage(loadError)}</p>
        <button type="button" className="button-secondary" onClick={fetchIntegration}>Retry</button>
      </StatusMessage>
    );
  }

  const showConsentSetup = state === 'not_configured' || pendingConsent;
  // A stored row decides the flow; with no row the operator picks the platform.
  const isGoogle = integration ? integration.provider === 'google_workspace' : providerChoice === 'google';

  return (
    <div className="tenant-search">
      <div className="tenant-search__toolbar">
        <h2 className="tenant-search__title">Tenant search</h2>
        <button
          type="button"
          className="button-secondary tenant-search__refresh"
          onClick={fetchIntegration}
          data-testid="tenant-search-refresh"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {!integration && !pendingConsent && (
        <div className="tenant-search__panel" data-testid="provider-choice-panel">
          <fieldset className="tenant-search__scope-choice" data-testid="provider-choice">
            <legend>Client platform</legend>
            <label className="tenant-search__scope-option">
              <input type="radio" name="provider-choice" value="m365" checked={providerChoice === 'm365'} onChange={() => setProviderChoice('m365')} data-testid="provider-choice-m365" />
              <span>
                <span className="tenant-search__scope-name">Microsoft 365</span>
                <span className="tenant-search__scope-desc">SharePoint document search through an admin-consented app. Broad or Selected scope.</span>
              </span>
            </label>
            <label className="tenant-search__scope-option">
              <input type="radio" name="provider-choice" value="google" checked={providerChoice === 'google'} onChange={() => setProviderChoice('google')} data-testid="provider-choice-google" />
              <span>
                <span className="tenant-search__scope-name">Google Workspace</span>
                <span className="tenant-search__scope-desc">Shared Drive search through a nominated account the client authorises for A2Z CareHub. No consent screen; a four-step checklist.</span>
              </span>
            </label>
          </fieldset>
        </div>
      )}

      {isGoogle ? (
        <GoogleWorkspaceEditor
          key={integration ? `${integration.tenantIntegrationId}:${integration.updatedAt || ''}` : 'new'}
          clientId={clientId}
          integration={integration}
          state={state}
          inFlight={inFlight}
          onSaved={(row) => { setIntegration(row); setPendingConsent(false); }}
          onToggleSearchActive={handleToggleSearchActive}
          onCopy={copyToClipboard}
        />
      ) : showConsentSetup ? (
        <ConsentSetupPanel
          state={state}
          pendingConsent={pendingConsent}
          scopeChoice={scopeChoice}
          consentTenantId={consentTenantId}
          consentTenantIdError={consentTenantIdError}
          consentInfo={consentInfo}
          inFlight={inFlight}
          onChangeScope={setScopeChoice}
          onChangeTenantId={(v) => { setConsentTenantId(v); if (consentTenantIdError) setConsentTenantIdError(null); }}
          onGenerate={handleGenerateConsentUrl}
          onCopy={copyToClipboard}
        />
      ) : (
        <>
          <IntegrationStatusHeader integration={integration} state={state} onViewErrors={viewErrorsInLog} />

          {(state === 'active' || state === 'degraded') && (
            <>
              {scopeModel === 'selected' && (
                <GrantScriptPanel
                  integration={integration}
                  resolveTextarea={resolveTextarea}
                  onCopy={copyToClipboard}
                />
              )}
              <ScopedSitesPicker
                integration={integration}
                scopeModel={scopeModel}
                sites={sites}
                pseudoSitesFiltered={pseudoSitesFiltered}
                sitesError={sitesError}
                siteSearchTerm={siteSearchTerm}
                selectedSiteIds={selectedSiteIds}
                resolveTextarea={resolveTextarea}
                resolvedSites={resolvedSites}
                droppedUrls={droppedUrls}
                inFlight={inFlight}
                onChangeSearchTerm={setSiteSearchTerm}
                onLoadSites={handleLoadSites}
                onToggleSite={toggleSite}
                onChangeResolveTextarea={setResolveTextarea}
                onResolve={handleResolveSites}
                onSave={handleSaveScopedSites}
                saveReason={saveReason}
                onChangeSaveReason={setSaveReason}
                onToggleSearchActive={handleToggleSearchActive}
              />
              <TestSearchPanel
                scopeModel={scopeModel}
                testQuery={testQuery}
                testResult={testResult}
                testError={testError}
                inFlight={inFlight}
                onChangeQuery={setTestQuery}
                onTest={handleTestSearch}
              />
            </>
          )}
        </>
      )}

      {/* Provider-neutral tail: the inactive notice, the search log and the
          revoke control serve both the M365 and the Google rows (2026-09-09). */}
      {integration && !showConsentSetup && (
        <>
          {(state === 'revoked' || state === 'expired') && (
            <div className="tenant-search__inactive" data-testid="tenant-search-inactive">
              <p>
                {state === 'revoked'
                  ? 'Tenant search is revoked for this client. Reactivate to resume (search stays off until you switch it on).'
                  : 'Tenant search expired after repeated search failures. Reactivate to resume (the failure counter resets; search stays off until you switch it on).'}
              </p>
              <button
                type="button"
                className="button-primary"
                onClick={() => setConfirmKind('reactivate')}
                data-testid="reactivate-button"
              >
                Reactivate
              </button>
            </div>
          )}

          <SearchLogViewer
            filters={logFilters}
            items={logItems}
            pagination={logPagination}
            offset={logOffset}
            loading={logLoading}
            error={logError}
            onFilterChange={updateLogFilter}
            onPrev={() => setLogOffset((o) => Math.max(0, o - LOG_LIMIT))}
            onNext={() => setLogOffset((o) => o + LOG_LIMIT)}
          />

          {(state === 'active' || state === 'degraded') && (
            <div className="tenant-search__danger-zone">
              <button
                type="button"
                className="button-secondary tenant-search__revoke"
                onClick={() => setConfirmKind('revoke')}
                data-testid="revoke-button"
              >
                Revoke tenant search
              </button>
            </div>
          )}
        </>
      )}

      {/* OUTSIDE the consent-setup ternary on purpose (ruled 2026-08-10):
          document indexing is a CLIENT-level capability. CareComply policy
          documents live in our own blob storage and need no M365 access, so
          a client with no tenant integration - the common case, and the one
          the ruling exists to serve - must still be able to switch it on.
          Rendering it only in the configured branch was the same coupling
          the ruling removed, one layer up. */}
      <IndexingPanel
        isIndexingActive={!!(indexing && indexing.isIndexingActive)}
        indexing={indexing}
        indexingError={indexingError}
        inFlight={inFlight}
        onToggleIndexingActive={handleToggleIndexingActive}
        onReindex={handleReindex}
        onRefresh={loadIndexingStatus}
      />

      <ReasonRequiredConfirmation
        open={confirmKind !== null}
        title={confirmKind === 'revoke' ? 'Revoke tenant search' : 'Reactivate tenant search'}
        description={confirmKind === 'revoke'
          ? 'Revoking stops all tenant document search for this client. Your curated sites are kept for when you reactivate. Give a reason for the audit log.'
          : 'Reactivating resumes tenant search and resets the failure counter. Search stays switched off until you turn it on. Give a reason for the audit log.'}
        actionLabel={confirmKind === 'revoke' ? 'Revoke' : 'Reactivate'}
        destructive={confirmKind === 'revoke'}
        isSubmitting={inFlight === 'revoke' || inFlight === 'reactivate'}
        onConfirm={(reason) => (confirmKind === 'revoke' ? handleRevoke(reason) : handleReactivate(reason))}
        onCancel={() => { if (inFlight !== 'revoke' && inFlight !== 'reactivate') setConfirmKind(null); }}
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// Inline sub-components
// ════════════════════════════════════════════════════════════

function ConsentSetupPanel({
  state, pendingConsent, scopeChoice, consentTenantId, consentTenantIdError, consentInfo, inFlight,
  onChangeScope, onChangeTenantId, onGenerate, onCopy,
}) {
  const choosingScope = state === 'not_configured' && !pendingConsent;
  return (
    <div className="tenant-search__panel tenant-search__consent" data-testid="consent-setup-panel">
      <h3 className="tenant-search__panel-title">Set up tenant search</h3>
      <p className="tenant-search__panel-intro">
        Tenant search lets CareIQ agents cite the client’s own SharePoint documents. Grant the
        A2Z CareHub Document Intel app read access to the client’s Microsoft 365 tenant to begin.
      </p>

      {choosingScope && (
        <fieldset className="tenant-search__scope-choice" data-testid="scope-choice">
          <legend>Integration scope</legend>
          <label className="tenant-search__scope-option">
            <input
              type="radio"
              name="scope-choice"
              value="broad"
              checked={scopeChoice === 'broad'}
              onChange={() => onChangeScope('broad')}
              data-testid="scope-choice-broad"
            />
            <span>
              <span className="tenant-search__scope-name">Broad — single-entity tenant</span>
              <span className="tenant-search__scope-desc">Single tenant, one client entity. Search works immediately after curation. Best for direct-managed clients.</span>
            </span>
          </label>
          <label className="tenant-search__scope-option">
            <input
              type="radio"
              name="scope-choice"
              value="selected"
              checked={scopeChoice === 'selected'}
              onChange={() => onChangeScope('selected')}
              data-testid="scope-choice-selected"
            />
            <span>
              <span className="tenant-search__scope-name">Selected — multi-entity shared tenant</span>
              <span className="tenant-search__scope-desc">Multiple client entities sharing one tenant. Live search is unavailable under this scope; these tenants are served by document content indexing instead.</span>
            </span>
          </label>
        </fieldset>
      )}

      {choosingScope && (
        <Field label="Client Microsoft 365 tenant ID" error={consentTenantIdError} required>
          <input
            type="text"
            value={consentTenantId}
            onChange={(e) => onChangeTenantId(e.target.value)}
            placeholder="aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee"
            autoComplete="off"
            data-testid="consent-tenant-id"
          />
        </Field>
      )}

      {!consentInfo ? (
        <button
          type="button"
          className="button-primary"
          onClick={onGenerate}
          disabled={inFlight === 'consent'}
          data-testid="generate-consent-url"
        >
          {inFlight === 'consent' ? 'Generating…' : 'Generate consent URL'}
        </button>
      ) : (
        <div className="tenant-search__consent-result">
          <p className="tenant-search__hint">
            Send this URL to the client tenant administrator (or open it yourself if you have
            Global Admin in their tenant). Once consent is granted, click <strong>Refresh</strong> above.
          </p>
          <div className="tenant-search__artifact">
            <pre className="tenant-search__code" data-testid="consent-url-display">{consentInfo.consentUrl}</pre>
            <button type="button" className="button-secondary" onClick={() => onCopy(consentInfo.consentUrl)} aria-label="Copy consent URL">
              <Copy size={14} /> Copy
            </button>
            <a href={consentInfo.consentUrl} target="_blank" rel="noopener noreferrer" className="button-secondary">
              <ExternalLink size={14} /> Open
            </a>
          </div>
          {consentInfo.expiresAt && (
            <p className="tenant-search__muted">Link expires {formatDate(consentInfo.expiresAt)}.</p>
          )}
        </div>
      )}
    </div>
  );
}

function IntegrationStatusHeader({ integration, state, onViewErrors }) {
  const degradedOrExpired = state === 'degraded' || state === 'expired';
  return (
    <div className="tenant-search__panel tenant-search__status-header" data-testid="integration-status-header">
      <div className="tenant-search__status-row">
        <TenantIntegrationStatusBadge state={state} consecutiveFailureCount={integration.consecutiveFailureCount} />
        {degradedOrExpired && (
          <button type="button" className="button-link" onClick={onViewErrors} data-testid="view-search-log-link">
            View search log for details
          </button>
        )}
      </div>
      <dl className="tenant-search__readonly">
        <dt>Tenant</dt>
        <dd>{integration.tenantId}</dd>
        <dt>Scope</dt>
        <dd>{getScopeLabel(integration.scopeModel)}</dd>
        <dt>Consent granted</dt>
        <dd>{integration.consentGrantedAt ? `${formatDate(integration.consentGrantedAt)} by ${integration.consentGrantedBy || '—'}` : '—'}</dd>
        <dt>Last successful search</dt>
        <dd>{integration.lastSuccessAt ? formatDate(integration.lastSuccessAt) : 'Never'}</dd>
        <dt>Consecutive failures</dt>
        <dd>{integration.consecutiveFailureCount || 0}</dd>
      </dl>
    </div>
  );
}

// Equal-membership comparison of two siteId arrays (order-independent).
function sameIdSet(a, b) {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((id) => setB.has(id));
}

// Friendly text for a Selected resolve-drop reason (from the MCP tool).
function dropReason(reason) {
  if (reason === 'not_granted') return 'Not yet granted — add it to the grant script above and re-run';
  if (reason === 'invalid format') return 'Invalid URL or site ID format';
  if (reason === 'system site (excluded)') return 'System site (excluded)';
  return reason || 'Could not resolve';
}

// Selected-only: the per-site grant script the client admin runs out-of-band.
function GrantScriptPanel({ integration, resolveTextarea, onCopy }) {
  const urls = resolveTextarea.split('\n').map((u) => u.trim()).filter(Boolean);
  const script = buildGrantScript(integration.tenantId, urls);
  return (
    <div className="tenant-search__panel tenant-search__grant" data-testid="grant-script-panel">
      <h3 className="tenant-search__panel-title">Grant document sites (Selected scope)</h3>
      <p className="tenant-search__panel-intro">
        Selected scope grants access per-site. A SharePoint or Global Administrator in the client
        tenant runs this script to grant the A2Z CareHub Document Intel (Selected) app read access
        to the chosen sites. Type the site URLs into the picker below and the script updates as you
        type; edit the <code>$siteUrls</code> list directly if you prefer.
      </p>
      <pre className="tenant-search__code tenant-search__grant-script" data-testid="grant-script">{script}</pre>
      <button type="button" className="button-secondary" onClick={() => onCopy(script)} aria-label="Copy grant script">
        <Copy size={14} /> Copy script
      </button>
      <p className="tenant-search__hint">
        After running this script as a SharePoint or Global Administrator in the client tenant,
        paste the same URLs into the Document sites picker below to resolve and curate.
      </p>
    </div>
  );
}

// Optional audit annotation on a scoped-sites save. Shown only when there is
// something to save — an input beside a disabled button invites typing into
// the void. One component, used by both the Broad and Selected branches, so
// the two cannot drift.
function SaveReasonField({ value, onChange, disabled }) {
  return (
    <label className="tenant-search__save-reason">
      <span className="tenant-search__muted">Reason (optional)</span>
      <input
        type="text"
        value={value || ''}
        maxLength={500}
        disabled={disabled}
        placeholder="e.g. added the new policies library"
        onChange={(e) => onChange(e.target.value)}
        data-testid="save-reason-input"
      />
    </label>
  );
}

function ScopedSitesPicker({
  integration, scopeModel, sites, pseudoSitesFiltered, sitesError, siteSearchTerm, selectedSiteIds,
  resolveTextarea, resolvedSites, droppedUrls, inFlight,
  onChangeSearchTerm, onLoadSites, onToggleSite, onChangeResolveTextarea, onResolve, onSave, onToggleSearchActive,
  saveReason, onChangeSaveReason,
}) {
  const isSelected = scopeModel === 'selected';
  const savedIds = (Array.isArray(integration.scopedSites) ? integration.scopedSites : []).map((s) => s.siteId);
  const savedCount = savedIds.length;

  // Dirty-state (polish #2): is the current curation different from what's saved?
  const currentIds = isSelected
    ? (resolvedSites || []).map((s) => s.siteId)
    : [...selectedSiteIds];
  const hasCuration = isSelected ? resolvedSites !== null : sites !== null;
  const isDirty = hasCuration && !sameIdSet(currentIds, savedIds);

  // Nudge (Broad only, polish #1): conditioned on savedCount + search-active.
  const level = (!isSelected && sites !== null)
    ? nudgeLevel(sites.length, savedCount, integration.isSearchActive)
    : null;
  // 30+-site gate (Broad): activation locked until ≥1 site is SAVED.
  const searchGateLocked = !isSelected && sites !== null && sites.length > SCOPED_SITES_GATE && savedCount === 0;
  // Single source of truth for "is the activation toggle disabled" (refinement 2):
  // Selected can never activate here — search is delivered by Phase 4 indexing.
  const toggleDisabled = inFlight === 'toggle-search' || searchGateLocked || isSelected;

  return (
    <div className="tenant-search__panel" data-testid="scoped-sites-picker">
      <h3 className="tenant-search__panel-title">Document sites</h3>

      {isSelected ? (
        <>
          <p className="tenant-search__panel-intro">
            Paste the SharePoint site URLs you granted via the script above (one per line), then
            Resolve to confirm access. The resolved sites become the search scope.
          </p>
          <textarea
            className="tenant-search__resolve-textarea"
            value={resolveTextarea}
            onChange={(e) => onChangeResolveTextarea(e.target.value)}
            rows={4}
            placeholder="https://tenant.sharepoint.com/sites/foo (one per line)"
            aria-label="Site URLs to resolve"
            data-testid="resolve-textarea"
          />
          <div className="tenant-search__site-loader">
            <button type="button" className="button-secondary" onClick={onResolve} disabled={inFlight === 'resolve-sites'} data-testid="resolve-sites">
              <Search size={14} /> {inFlight === 'resolve-sites' ? 'Resolving…' : 'Resolve sites'}
            </button>
          </div>

          {sitesError && (
            <div className="tenant-search__inline-error" role="alert" data-testid="sites-error">
              <AlertCircle size={16} /> <span>{mcpErrorText(sitesError)}</span>
            </div>
          )}

          {resolvedSites !== null && (
            <>
              {resolvedSites.length === 0 && droppedUrls.length === 0 ? (
                <p className="tenant-search__hint" data-testid="resolve-empty">No sites resolved. Paste the granted site URLs above and click Resolve.</p>
              ) : (
                <ul className="tenant-search__site-list" data-testid="resolved-site-list">
                  {resolvedSites.map((s) => (
                    <li key={s.siteId} className="tenant-search__site-row">
                      <span className="tenant-search__resolve-ok"><CheckCircle2 size={14} /> {s.siteName || s.siteId}</span>
                      <span className="tenant-search__site-url">{s.siteUrl}</span>
                    </li>
                  ))}
                  {droppedUrls.map((d, i) => (
                    <li key={`dropped-${i}`} className="tenant-search__site-row tenant-search__site-row--dropped" data-testid="dropped-url-row">
                      <span className="tenant-search__resolve-bad"><AlertCircle size={14} /> {d.url}</span>
                      <span className="tenant-search__site-url">{dropReason(d.reason)}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="tenant-search__picker-actions">
                {isDirty && <span className="tenant-search__muted">{resolvedSites.length} site{resolvedSites.length === 1 ? '' : 's'} to save</span>}
                {isDirty && <SaveReasonField value={saveReason} onChange={onChangeSaveReason} disabled={inFlight === 'save-sites'} />}
                <button type="button" className="button-primary" onClick={onSave} disabled={inFlight === 'save-sites' || !isDirty} data-testid="save-scoped-sites">
                  {inFlight === 'save-sites' ? 'Saving…' : 'Save sites'}
                </button>
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <p className="tenant-search__panel-intro">
            Choose which of the tenant’s SharePoint sites agents search. Leaving this empty searches
            all sites (Broad default) — fine for small tenants, slow for large ones.
          </p>

          <div className="tenant-search__site-loader">
            <input
              type="text"
              value={siteSearchTerm}
              onChange={(e) => onChangeSearchTerm(e.target.value)}
              placeholder="Filter sites (optional)"
              aria-label="Filter sites"
              data-testid="site-search-term"
            />
            <button type="button" className="button-secondary" onClick={onLoadSites} disabled={inFlight === 'list-sites'} data-testid="load-sites">
              <Search size={14} /> {inFlight === 'list-sites' ? 'Loading…' : 'Load sites'}
            </button>
          </div>

          {sitesError && (
            <div className="tenant-search__inline-error" role="alert" data-testid="sites-error">
              <AlertCircle size={16} /> <span>{mcpErrorText(sitesError)}</span>
            </div>
          )}

          {sites !== null && (
            <>
              {level && level !== 'hidden' && (
                <div className={`tenant-search__nudge tenant-search__nudge--${level}`} role="status" data-testid="site-nudge">
                  <AlertTriangle size={16} />
                  <span>
                    {level === 'curated'
                      ? `${savedCount} of ${sites.length} site${sites.length === 1 ? '' : 's'} curated. Switch search on below when ready.`
                      : <>{sites.length} site{sites.length === 1 ? '' : 's'} found{pseudoSitesFiltered > 0 ? ` (${pseudoSitesFiltered} system sites hidden)` : ''}. {NUDGE_TEXT[level]}</>}
                  </span>
                </div>
              )}

              {sites.length === 0 ? (
                <StatusMessage as="p" variant="empty" className="tenant-search__hint">No sites returned. The tenant may have no document sites, or consent may be incomplete.</StatusMessage>
              ) : (
                <ul className="tenant-search__site-list" data-testid="site-list">
                  {sites.map((s) => (
                    <li key={s.siteId} className="tenant-search__site-row">
                      <label className="tenant-search__site-label">
                        <input
                          type="checkbox"
                          checked={selectedSiteIds.has(s.siteId)}
                          onChange={() => onToggleSite(s.siteId)}
                        />
                        <span className="tenant-search__site-name">{s.siteName || s.siteId}</span>
                        <span className="tenant-search__site-url">{s.siteUrl}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}

              <div className="tenant-search__picker-actions">
                {isDirty && <span className="tenant-search__muted">{selectedSiteIds.size} selected</span>}
                {isDirty && <SaveReasonField value={saveReason} onChange={onChangeSaveReason} disabled={inFlight === 'save-sites'} />}
                <button type="button" className="button-primary" onClick={onSave} disabled={inFlight === 'save-sites' || !isDirty} data-testid="save-scoped-sites">
                  {inFlight === 'save-sites' ? 'Saving…' : 'Save selected sites'}
                </button>
              </div>
            </>
          )}
        </>
      )}

      <div className="tenant-search__toggles">
        <div className="tenant-search__toggle-row">
          <Switch
            checked={!!integration.isSearchActive}
            onChange={onToggleSearchActive}
            disabled={toggleDisabled}
            ariaLabel="Search active"
            id="tenant-search-is-search-active"
          />
          <label htmlFor="tenant-search-is-search-active" className="tenant-search__toggle-label">
            Search active
            {isSelected ? (
              <span className="tenant-search__toggle-hint tenant-search__toggle-hint--info" data-testid="selected-search-deferred">
                {' '}— live search is not available under Selected scope. This tenant's documents are searched through Document content indexing below.
              </span>
            ) : searchGateLocked && (
              <span className="tenant-search__toggle-hint" data-testid="search-gate-hint">
                {' '}— save at least one site before activating search
              </span>
            )}
          </label>
        </div>

        {/* Phase 4 document-content indexing lives in its own panel below —
            it is a different capability from live search, with its own state
            and its own re-index action, and pairing them in one row implied
            they were two settings of one thing. */}
      </div>
    </div>
  );
}

function IndexingPanel({ isIndexingActive, indexing, indexingError, inFlight, onToggleIndexingActive, onReindex, onRefresh }) {
  // Single disabled source (the house convention): one named variable per
  // control rather than scattered checks that drift apart.
  const toggleBusy = inFlight === 'toggle-indexing';
  const reindexDisabled = inFlight === 'reindex' || !isIndexingActive;
  const summary = (indexing && indexing.summary) || null;
  const runs = (indexing && indexing.recentRuns) || [];
  const failedRuns = runs.filter((r) => r.Status === 'failed');

  return (
    <div className="tenant-search__panel" data-testid="indexing-panel">
      <h3 className="tenant-search__panel-title">Document content indexing</h3>
      <p className="tenant-search__panel-intro">
        Indexes the content of this organisation's policy documents so agents can quote them,
        not just name them. Independent of live search: a Selected-scope tenant is indexed and
        never live-searched.
      </p>

      <div className="tenant-search__toggle-row">
        <Switch
          checked={isIndexingActive}
          onChange={() => onToggleIndexingActive(!isIndexingActive)}
          disabled={toggleBusy}
          ariaLabel="Document content indexing"
          id="tenant-search-is-indexing-active"
        />
        <label htmlFor="tenant-search-is-indexing-active" className="tenant-search__toggle-label">
          Document content indexing
        </label>
      </div>

      {indexingError && (
        <div className="tenant-search__inline-error" role="alert" data-testid="indexing-error">
          <AlertCircle size={16} /> <span>{indexingError}</span>
        </div>
      )}

      {isIndexingActive && summary && (
        <>
          <dl className="tenant-search__facts" data-testid="indexing-facts">
            <div><dt>Documents indexed</dt><dd>{summary.documentCount}</dd></div>
            <div><dt>Passages</dt><dd>{summary.chunkCount}</dd></div>
            <div><dt>Last indexed</dt><dd>{summary.lastIndexedAt ? new Date(summary.lastIndexedAt).toLocaleString('en-GB') : 'Never'}</dd></div>
            <div><dt>Last 7 days</dt><dd>{summary.last7Days.success} indexed, {summary.last7Days.skipped} skipped, {summary.last7Days.failed} failed</dd></div>
            {summary.bySource && (
              <div data-testid="indexing-by-source">
                <dt>By source</dt>
                <dd>
                  {summary.bySource.carecomply || 0} CareComply, {summary.bySource.sharepoint || 0} SharePoint, {summary.bySource.google_drive || 0} Google Drive
                </dd>
              </div>
            )}
          </dl>

          {summary.documentCount === 0 && (
            <StatusMessage as="p" variant="empty" className="tenant-search__hint" data-testid="indexing-empty">
              Nothing is indexed yet. Use Re-index to ingest the current version of every active policy.
            </StatusMessage>
          )}

          {failedRuns.length > 0 && (
            <div className="tenant-search__inline-error" role="alert" data-testid="indexing-failures">
              <AlertTriangle size={16} />
              <span>
                {failedRuns.length} document(s) failed to index. Most recent: {failedRuns[0].DocumentName || 'unnamed document'}
                {failedRuns[0].ErrorMessage ? ` — ${failedRuns[0].ErrorMessage}` : ''}
              </span>
            </div>
          )}

          {indexing.documentsTruncated && (
            <StatusMessage as="p" variant="empty" className="tenant-search__hint" data-testid="indexing-truncated">
              Showing the {indexing.documentsMaxRows} most recently indexed documents. Older documents are indexed but not listed here.
            </StatusMessage>
          )}
        </>
      )}

      <div className="tenant-search__site-loader">
        <button
          type="button"
          className="button-secondary"
          onClick={onReindex}
          disabled={reindexDisabled}
          data-testid="reindex-documents"
          title={isIndexingActive ? 'Queue every active policy for re-indexing' : 'Switch indexing on first'}
        >
          {inFlight === 'reindex' ? 'Queueing…' : 'Re-index documents'}
        </button>
        <button type="button" className="button-secondary" onClick={onRefresh} disabled={inFlight === 'reindex'} data-testid="refresh-indexing">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {isIndexingActive && indexing && indexing.sharePointSupported === false && null}
    </div>
  );
}

function TestSearchPanel({ scopeModel, testQuery, testResult, testError, inFlight, onChangeQuery, onTest }) {
  const d = testResult && testResult.diagnostics;
  const isSelected = scopeModel === 'selected';
  // Single disabled source (refinement 2): Test search depends on the live
  // drive-search path, which is unavailable under Sites.Selected (it 500s).
  const testDisabled = inFlight === 'test-search' || isSelected;
  return (
    <div className="tenant-search__panel" data-testid="test-search-panel">
      <h3 className="tenant-search__panel-title">Test search</h3>
      <p className="tenant-search__panel-intro">Run a live diagnostic search (not cached, not logged) to verify the integration works.</p>
      <div className="tenant-search__site-loader">
        <input
          type="text"
          value={testQuery}
          onChange={(e) => onChangeQuery(e.target.value)}
          placeholder="Query"
          aria-label="Test search query"
          disabled={testDisabled}
          data-testid="test-search-query"
        />
        <button type="button" className="button-secondary" onClick={onTest} disabled={testDisabled} data-testid="run-test-search">
          {inFlight === 'test-search' ? 'Testing…' : 'Test search'}
        </button>
      </div>
      {isSelected && (
        <p className="tenant-search__hint" data-testid="selected-test-deferred">
          Test search is not available under Selected scope. Document access is verified at
          indexing time — see Document content indexing below.
        </p>
      )}

      {testError && (
        <div className="tenant-search__inline-error" role="alert" data-testid="test-search-error">
          <AlertCircle size={16} /> <span>{mcpErrorText(testError)}</span>
        </div>
      )}

      {d && (
        <div className="tenant-search__diagnostics" role="status" data-testid="test-search-diagnostics">
          <div className="tenant-search__diag-headline">
            {testResult.success
              ? <span className="tenant-search__diag-ok"><CheckCircle2 size={16} /> All sites reachable</span>
              : <span className="tenant-search__diag-warn"><AlertTriangle size={16} /> Some sites unreachable</span>}
          </div>
          <dl className="tenant-search__readonly">
            <dt>Auth status</dt><dd>{d.authStatus}</dd>
            <dt>Scope</dt><dd>{getScopeLabel(d.scopeModelUsed)}</dd>
            <dt>Sites configured</dt><dd>{d.sitesConfigured}</dd>
            <dt>Reachable</dt><dd>{d.sitesReachable}</dd>
            <dt>Unreachable</dt><dd>{Array.isArray(d.sitesUnreachable) ? d.sitesUnreachable.length : (d.sitesUnreachable || 0)}</dd>
            {typeof d.pseudoSitesFiltered === 'number' && (<><dt>System sites hidden</dt><dd>{d.pseudoSitesFiltered}</dd></>)}
            {typeof d.latencyMs === 'number' && (<><dt>Latency</dt><dd>{d.latencyMs} ms</dd></>)}
          </dl>
        </div>
      )}
    </div>
  );
}

function SearchLogViewer({ filters, items, pagination, offset, loading, error, onFilterChange, onPrev, onNext }) {
  const returned = pagination ? pagination.returned : items.length;
  const first = items.length === 0 ? 0 : offset + 1;
  const last = offset + returned;
  const prevDisabled = offset === 0;
  const nextDisabled = returned < LOG_LIMIT;

  return (
    <div className="tenant-search__panel" id="tenant-search-log" data-testid="search-log-viewer">
      <h3 className="tenant-search__panel-title">Search log</h3>

      <div className="tenant-search__log-filters" data-testid="search-log-filters">
        <label>
          Agent
          <input
            type="text"
            value={filters.agentType}
            onChange={(e) => onFilterChange({ agentType: e.target.value })}
            placeholder="Any"
            data-testid="log-filter-agent"
          />
        </label>
        <label>
          From
          <input type="date" value={filters.fromDate} onChange={(e) => onFilterChange({ fromDate: e.target.value })} data-testid="log-filter-from" />
        </label>
        <label>
          To
          <input type="date" value={filters.toDate} onChange={(e) => onFilterChange({ toDate: e.target.value })} data-testid="log-filter-to" />
        </label>
        <label className="tenant-search__log-only-errors">
          <input
            type="checkbox"
            checked={filters.onlyErrors}
            onChange={(e) => onFilterChange({ onlyErrors: e.target.checked })}
            data-testid="log-filter-only-errors"
          />
          Errors only
        </label>
      </div>

      {error ? (
        <div className="tenant-search__inline-error" role="alert">
          <AlertCircle size={16} /> <span>{crudErrorMessage(error)}</span>
        </div>
      ) : loading ? (
        <StatusMessage as="p" variant="loading" className="tenant-search__hint"><Loader2 size={14} className="tenant-search__spinner" /> Loading…</StatusMessage>
      ) : items.length === 0 ? (
        <StatusMessage as="p" variant="empty" className="tenant-search__hint" data-testid="search-log-empty">No searches logged yet.</StatusMessage>
      ) : (
        <>
          <table className="tenant-search__log-table" data-testid="search-log-table">
            <thead>
              <tr><th>Agent</th><th>Query</th><th>Results</th><th>Error</th><th>When</th></tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.tenantSearchLogId} className={row.errorMessage ? 'tenant-search__log-row--error' : ''}>
                  <td>{row.agentType}</td>
                  <td className="tenant-search__log-query">{row.query}</td>
                  <td>{row.resultCount}</td>
                  <td className="tenant-search__log-error">{row.errorMessage ? String(row.errorMessage).slice(0, 80) : '—'}</td>
                  <td>{formatDate(row.searchedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="tenant-search__pagination">
            <span className="tenant-search__muted">Showing {first}–{last}</span>
            <div className="tenant-search__pagination-buttons">
              <button type="button" className="button-secondary" onClick={onPrev} disabled={prevDisabled}>Previous</button>
              <button type="button" className="button-secondary" onClick={onNext} disabled={nextDisabled}>Next</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ReasonRequiredConfirmation({ open, title, description, actionLabel, destructive, isSubmitting, onConfirm, onCancel }) {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open]);
  const canConfirm = reason.trim().length > 0 && !isSubmitting;
  return (
    <Modal isOpen={open} onClose={onCancel} title={title} closeDisabled={isSubmitting}>
      <div className="tenant-search__reason-modal">
        <p className="tenant-search__reason-desc">{description}</p>
        <Field label="Reason" required>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="Why are you making this change?"
            data-testid="reason-input"
          />
        </Field>
        <div className="tenant-search__modal-actions">
          <button type="button" className="button-secondary" onClick={onCancel} disabled={isSubmitting}>Cancel</button>
          <button
            type="button"
            className={destructive ? 'button-destructive' : 'button-primary'}
            onClick={() => onConfirm(reason.trim())}
            disabled={!canConfirm}
            data-testid="reason-confirm"
          >
            {isSubmitting ? '…' : actionLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}
